<script lang="ts">
  import type { Attachment } from '../lib/types';
  import AttachmentPreview from './AttachmentPreview.svelte';

  interface Props {
    placeholder: string;
    sendLabel: string;
    attachLabel: string;
    enterToSendLabel?: string;
    disabled?: boolean;
    /** When false, hides the paperclip button + file input entirely. */
    showAttach?: boolean;
    pendingAttachments: Attachment[];
    onsend: (text: string) => void;
    onattach: (files: FileList) => void;
    onremoveattachment: (att: Attachment) => void;
  }

  let {
    placeholder,
    sendLabel,
    attachLabel,
    enterToSendLabel = '⏎ para enviar',
    disabled = false,
    showAttach = true,
    pendingAttachments,
    onsend,
    onattach,
    onremoveattachment,
  }: Props = $props();

  let text = $state('');
  let fileInput: HTMLInputElement | undefined = $state();
  let textarea: HTMLTextAreaElement | undefined = $state();

  const canSend = $derived(
    !disabled && (text.trim().length > 0 || pendingAttachments.length > 0),
  );

  const hasContent = $derived(text.length > 0);

  // Autogrow textarea between min (40) and max (140) height.
  $effect(() => {
    const _t = text;
    void _t;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const next = Math.min(140, Math.max(40, textarea.scrollHeight));
    textarea.style.height = `${next}px`;
  });

  function submit(): void {
    if (!canSend) return;
    const trimmed = text.trim();
    onsend(trimmed);
    text = '';
    // Reset height after clear
    queueMicrotask(() => {
      if (textarea) textarea.style.height = '40px';
    });
  }

  function handleKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  function handleFilePick(e: Event): void {
    const input = e.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      onattach(input.files);
      input.value = '';
    }
  }
</script>

<form
  class="hd-composer"
  onsubmit={(e) => {
    e.preventDefault();
    submit();
  }}
>
  {#if pendingAttachments.length > 0}
    <div class="hd-composer__attachments">
      {#each pendingAttachments as att (att.storage_key)}
        <AttachmentPreview attachment={att} onremove={onremoveattachment} />
      {/each}
    </div>
  {/if}

  <div class="hd-composer__field">
    <textarea
      class="hd-composer__input"
      rows="1"
      {placeholder}
      bind:value={text}
      bind:this={textarea}
      onkeydown={handleKeydown}
      {disabled}
      aria-label={placeholder}
    ></textarea>

    <div class="hd-composer__toolbar">
      {#if showAttach}
        <button
          type="button"
          class="hd-composer__attach"
          aria-label={attachLabel}
          title={attachLabel}
          onclick={() => fileInput?.click()}
          disabled={disabled}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M21 12.5 12.5 21a5 5 0 0 1-7.07-7.07l9.19-9.19a3.5 3.5 0 1 1 4.95 4.95l-9.2 9.19a2 2 0 1 1-2.83-2.83l8.49-8.48"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <input
          type="file"
          multiple
          accept="image/*,application/pdf,video/*"
          class="hd-composer__file-input"
          hidden
          bind:this={fileInput}
          onchange={handleFilePick}
        />
      {/if}

      <div class="hd-composer__spacer">
        {#if hasContent}
          <span class="hd-composer__hint">{enterToSendLabel}</span>
        {/if}
      </div>

      <button
        type="submit"
        class="hd-composer__send"
        disabled={!canSend}
        aria-label={sendLabel}
        title={sendLabel}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M3.4 20.4 22 12 3.4 3.6 3.4 10.4 16 12 3.4 13.6Z" fill="currentColor" />
        </svg>
      </button>
    </div>
  </div>
</form>

<style>
  .hd-composer {
    border-top: 1px solid var(--hd-border, #e2e8f0);
    padding: 12px;
    background: var(--hd-surface, #ffffff);
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 0 0 auto;
  }
  .hd-composer__attachments {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .hd-composer__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    border: 1px solid var(--hd-border, #e2e8f0);
    border-radius: 12px;
    background: var(--hd-surface, #ffffff);
    padding: 2px 2px 6px;
    transition:
      border-color 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
      box-shadow 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  .hd-composer__field:focus-within {
    border-color: var(--hd-primary, #2563eb);
    box-shadow: 0 0 0 3px rgb(var(--hd-primary-rgb, 37 99 235) / 0.15);
  }
  .hd-composer__input {
    width: 100%;
    flex: 1 1 auto;
    min-height: 40px;
    max-height: 140px;
    resize: none;
    border: none;
    border-radius: 10px;
    padding: 10px 12px;
    font: inherit;
    font-size: 14px;
    line-height: 1.5;
    color: var(--hd-fg, #0f172a);
    background: transparent;
    outline: none;
    box-sizing: border-box;
  }
  .hd-composer__input::placeholder {
    color: var(--hd-fg-subtle, #94a3b8);
  }
  .hd-composer__toolbar {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 6px;
  }
  .hd-composer__file-input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
    opacity: 0;
    pointer-events: none;
  }
  .hd-composer__spacer {
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
  }
  .hd-composer__hint {
    font-size: 11px;
    font-weight: 500;
    color: var(--hd-fg-subtle, #94a3b8);
    animation: hd-hint-in 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  @keyframes hd-hint-in {
    from {
      opacity: 0;
      transform: translateY(2px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .hd-composer__attach {
    flex: 0 0 auto;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    display: grid;
    place-items: center;
    color: var(--hd-fg-muted, #64748b);
    background: transparent;
    transition:
      background-color 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
      color 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  .hd-composer__attach:hover:not([disabled]) {
    background: var(--hd-surface-muted, #f8fafc);
    color: var(--hd-fg, #0f172a);
  }
  .hd-composer__attach:focus-visible,
  .hd-composer__send:focus-visible {
    outline: 2px solid var(--hd-primary, #2563eb);
    outline-offset: 2px;
  }
  .hd-composer__send {
    flex: 0 0 auto;
    width: 32px;
    height: 32px;
    border-radius: 9999px;
    border: none;
    cursor: pointer;
    display: grid;
    place-items: center;
    color: var(--hd-primary-fg, #ffffff);
    background: var(--hd-primary, #2563eb);
    transition:
      transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
      opacity 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
      box-shadow 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
    box-shadow: 0 1px 2px rgb(var(--hd-primary-rgb, 37 99 235) / 0.3);
  }
  .hd-composer__send:hover:not([disabled]) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px -2px rgb(var(--hd-primary-rgb, 37 99 235) / 0.35);
  }
  .hd-composer__send:active:not([disabled]) {
    transform: scale(0.96);
  }
  .hd-composer__send:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    box-shadow: none;
  }
  .hd-composer__attach svg {
    width: 16px;
    height: 16px;
  }
  .hd-composer__send svg {
    width: 14px;
    height: 14px;
    transform: translate(-1px, 0);
  }
  @media (prefers-reduced-motion: reduce) {
    .hd-composer__field,
    .hd-composer__attach,
    .hd-composer__send,
    .hd-composer__hint {
      transition: none;
      animation: none;
    }
  }
</style>
